<template>
  <section v-if="loading" class="packages-empty packages-empty--loading">
    <span class="packages-empty__spinner" aria-hidden="true" />
    Pakete werden geladen…
  </section>

  <section v-else-if="rows.length" class="packages-rows">
    <header class="packages-rows__intro">
      <h2 class="packages-rows__heading">Verfügbare Pakete</h2>
      <p class="packages-rows__lead mb-0">
        Wählen Sie ein Paket passend zu Ihrem Vermarktungsbereich.
      </p>
    </header>

    <article
      v-for="row in rows"
      :key="row.key"
      :id="`package-row-${row.key}`"
      class="packages-row"
      :class="getBaseTypeThemeClass(row.key)"
      :style="getBaseTypeThemeStyle(row.key)"
    >
      <header class="packages-row__header">
        <span class="packages-row__icon" aria-hidden="true">
          <Icon :icon="getRowIcon(row.key)" />
        </span>
        <div class="packages-row__copy">
          <h3 class="packages-row__title">{{ row.label }}</h3>
          <p class="packages-row__teaser">{{ row.teaser }}</p>
        </div>
      </header>

      <div
        v-if="row.packages.length"
        class="packages-row__tiles"
        :style="rowTilesStyle(row.packages.length)"
      >
        <PackageTile
          v-for="(pkg, index) in row.packages"
          :key="`${row.key}-${getPackageIdentityKey(pkg)}-${index}`"
          :row="row"
          :pkg="pkg"
          :index="index"
          :route-query="routeQuery"
        />
      </div>
      <p v-else class="packages-row__empty mb-0">
        Aktuell sind keine Pakete für {{ row.label.toLowerCase() }} verfügbar.
      </p>
    </article>
  </section>

  <section v-else class="packages-empty">
    Aktuell sind keine Pakete verfügbar. Schauen Sie später noch einmal vorbei.
  </section>
</template>

<script>
import PackageTile from '@/components/packages/PackageTile.vue'
import {
  getBaseTypeTheme,
  getBaseTypeThemeClass,
  getBaseTypeThemeStyle,
} from '@/utils/baseTypeTheme'
import {getPackageIdentityKey} from '@/utils/packagePolicy'
import {useBootstrapPopovers} from '@/composables/useBootstrapPopovers'

export default {
  name: 'PackagesCatalog',
  components: {PackageTile},
  props: {
    rows: {type: Array, required: true},
    loading: {type: Boolean, default: false},
    routeQuery: {type: Object, default: () => ({})},
  },
  setup() {
    useBootstrapPopovers()
  },
  methods: {
    getBaseTypeThemeClass,
    getBaseTypeThemeStyle,
    getPackageIdentityKey,
    getRowIcon(rowKey) {
      return getBaseTypeTheme(rowKey).icon
    },
    rowTilesStyle(packageCount) {
      const columns = Math.max(packageCount, 1)
      return {
        '--packages-row-columns': columns,
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      }
    },
  },
}
</script>

<style scoped lang="scss">
.packages-rows {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  margin-top: 0.35rem;
}

.packages-rows__intro {
  padding-top: 1.15rem;
  border-top: 1px solid #e3e9f0;
}

.packages-rows__heading {
  margin: 0 0 0.35rem;
  font-size: 1.12rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #10273d;
}

.packages-rows__lead {
  max-width: 52ch;
  color: #5d7183;
  font-size: 0.94rem;
  line-height: 1.45;
}

.packages-row {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.15rem 1.2rem 1.25rem;
  border: 1px solid var(--base-type-border, #d8e1ea);
  border-top: 0.28rem solid var(--base-type-primary, #d8e1ea);
  background: color-mix(in srgb, var(--base-type-surface, #ffffff) 68%, #ffffff);
  box-shadow: 0 10px 24px rgba(10, 33, 48, 0.05);
  scroll-margin-top: 5.5rem;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 14px 30px rgba(10, 33, 48, 0.07);
  }
}

.packages-row__header {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.packages-row__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  background: var(--base-type-soft, #eef2f6);
  color: var(--base-type-primary, #4a5f71);
  font-size: 1.2rem;
}

.packages-row__title {
  margin: 0;
  font-size: 1.18rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--base-type-text, #10273d);
}

.packages-row__teaser {
  margin: 0.25rem 0 0;
  color: #5d7183;
  font-size: 0.92rem;
  line-height: 1.45;
}

.packages-row__tiles {
  display: grid;
  grid-template-columns: repeat(var(--packages-row-columns, 1), minmax(0, 1fr));
  gap: 0.85rem;
}

.packages-row__tiles > * {
  min-width: 0;
}

.packages-row__empty {
  padding: 0.85rem 1rem;
  border: 1px dashed color-mix(in srgb, var(--base-type-border, #cdd8e3) 85%, #cdd8e3);
  background: color-mix(in srgb, var(--base-type-soft, #f5f8fb) 35%, #ffffff);
  color: #4e6477;
  font-size: 0.92rem;
}

.packages-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.65rem;
  padding: 1.35rem 1.25rem;
  border: 1px solid #e3e9f0;
  background: #f7fafc;
  color: #4e6477;
  font-size: 0.95rem;
  text-align: center;
}

.packages-empty--loading {
  min-height: 6rem;
}

.packages-empty__spinner {
  width: 1.1rem;
  height: 1.1rem;
  border: 2px solid #c5d3df;
  border-top-color: #1f6f8b;
  border-radius: 50%;
  animation: packages-catalog-spin 0.75s linear infinite;
}

@keyframes packages-catalog-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 575px) {
  .packages-row {
    padding: 1rem;
  }

  .packages-row__tiles {
    --packages-row-columns: 1;
    grid-template-columns: 1fr;
  }
}
</style>
